import { GapRentalFormValues } from "@/app/GapRentals/Utils/getDefaultGapRentalValues";

export type GapRentalDistanceMode = "UNLIMITED" | "PER_UNIT" | "FLAT";

type StoredDistance = {
  distance_allowed?: number | null;
  extra_distance_allowed_per_extra_hire_unit?: number | null;
};

/**
 * A relocation stores distance as a flat allowance plus a per-extra-unit
 * allowance that the booking sums, and reads a null in either as unlimited. A
 * gap rental only ever offers one of them, so the form works in a mode and a
 * single number — these translate between the two.
 */
export function getGapRentalDistanceMode(
  relocation: StoredDistance,
): GapRentalDistanceMode {
  if (
    relocation.distance_allowed == null ||
    relocation.extra_distance_allowed_per_extra_hire_unit == null
  ) {
    return "UNLIMITED";
  }

  // A flat allowance zeroes the per-unit field, so a zero there is the flat
  // mode — including a flat allowance of zero, which includes no distance.
  return (relocation.extra_distance_allowed_per_extra_hire_unit ?? 0) > 0
    ? "PER_UNIT"
    : "FLAT";
}

export function getGapRentalDistanceValue(
  relocation: StoredDistance,
): number | null {
  switch (getGapRentalDistanceMode(relocation)) {
    case "FLAT":
      return relocation.distance_allowed ?? null;
    case "PER_UNIT":
      return relocation.extra_distance_allowed_per_extra_hire_unit ?? null;
    default:
      return null;
  }
}

export type GapRentalInput = Omit<
  GapRentalFormValues,
  "distance_mode" | "distance_value"
>;

/**
 * Drops the form-only distance fields and writes the relocation's own distance
 * fields from them. Every gap rental submit goes through this — the mutation
 * input has no place for the form's own state.
 */
export function toGapRentalInput(values: GapRentalFormValues): GapRentalInput {
  const { distance_mode, distance_value, ...input } = values;

  if (distance_mode === "UNLIMITED") {
    return {
      ...input,
      distance_allowed: null,
      extra_distance_allowed_per_extra_hire_unit: null,
      // Nothing to charge against without a cap.
      excess_distance_rate: null,
    };
  }

  if (distance_mode === "PER_UNIT") {
    return {
      ...input,
      distance_allowed: 0,
      extra_distance_allowed_per_extra_hire_unit: distance_value ?? null,
    };
  }

  return {
    ...input,
    distance_allowed: distance_value ?? null,
    extra_distance_allowed_per_extra_hire_unit: 0,
  };
}
